import { parseFuturesSymbol } from "./helpersFutures";

const channelToSubscription = new Map();

let socket = null;
let futuresUpdateHandler = null;
let listenerAttached = false;
let pendingStreamParams = null;

// ─── Socket handoff ───────────────────────────────────────────────────────────

/**
 * Called by FuturesChartContainer once the shared SocketContext socket is ready.
 * Attaches the futures:update listener on the existing connection — no new socket.
 */
export function setSharedSocket(socketInstance) {
  if (!socketInstance) return;
  if (socket === socketInstance) {
    // Same socket — just ensure listener is attached if we have active subscriptions
    if (channelToSubscription.size > 0) ensureFuturesListener();
    return;
  }

  // New socket instance — remove old listener first
  if (socket && futuresUpdateHandler) {
    socket.off("futures:update", futuresUpdateHandler);
    futuresUpdateHandler = null;
    listenerAttached = false;
  }

  socket = socketInstance;

  // Apply any pending subscription that arrived before the socket was ready
  if (pendingStreamParams) {
    _setupStream(pendingStreamParams);
    pendingStreamParams = null;
  } else if (channelToSubscription.size > 0) {
    ensureFuturesListener();
  }
}

// ─── Single persistent listener ───────────────────────────────────────────────

function handlePriceTick(price, tickTimeMs, symbol) {
  for (const [channelString, subscriptionItem] of channelToSubscription) {
    // Normalise both sides to "BTCUSDT_PERP" style for comparison
    const channelSymbol = channelString.replace(/-/g, "_").toUpperCase();
    const tickSymbol    = (symbol || "").replace(/-/g, "_").toUpperCase();
    if (tickSymbol && channelSymbol && tickSymbol !== channelSymbol) continue;

    if (!subscriptionItem.lastDailyBar) {
      subscriptionItem.lastDailyBar = {
        time:   getStartOfMinute(tickTimeMs),
        open:   price,
        high:   price,
        low:    price,
        close:  price,
        volume: 0,
      };
      subscriptionItem.handlers.forEach((h) => h.callback(subscriptionItem.lastDailyBar));
      continue;
    }

    const lastBarMinute     = getStartOfMinute(subscriptionItem.lastDailyBar.time);
    const currentTickMinute = getStartOfMinute(tickTimeMs);
    let bar;

    if (currentTickMinute > lastBarMinute) {
      bar = {
        time:   currentTickMinute,
        open:   subscriptionItem.lastDailyBar.close,
        high:   price,
        low:    price,
        close:  price,
        volume: 0,
      };
    } else {
      bar = {
        ...subscriptionItem.lastDailyBar,
        high:  Math.max(subscriptionItem.lastDailyBar.high, price),
        low:   Math.min(subscriptionItem.lastDailyBar.low,  price),
        close: price,
      };
    }

    subscriptionItem.lastDailyBar = bar;
    subscriptionItem.handlers.forEach((h) => h.callback(bar));
  }
}

function ensureFuturesListener() {
  if (!socket || listenerAttached || futuresUpdateHandler) return;
  if (channelToSubscription.size === 0) return;

  futuresUpdateHandler = (data) => {
    try {
      if (!data) return;

      // Sub-second price tick: { symbol, mark_price, last_price, ... }
      // This event may be absent — the primary feed is futures:update contract_data.
      if (!data.type) {
        const price = parseFloat(data.mark_price ?? data.last_price);
        if (!price || !Number.isFinite(price)) return;
        handlePriceTick(price, data.timestamp || Date.now(), data.symbol || "");
        return;
      }

      // contracts_only — no price data, skip
      if (data.type === "contracts_only") return;

      // contract_data — price lives inside data.contract
      if (data.type === "contract_data" && data.contract) {
        const c = data.contract;
        const price = parseFloat(c.mark_price ?? c.last_price ?? c.bid_price);
        if (!price || !Number.isFinite(price)) return;
        handlePriceTick(price, data.timestamp || Date.now(), c.symbol || "");
        return;
      }
    } catch (_) {
      // Prevent chart crashes
    }
  };

  // Listen on futures:update (1.5s contract_data ticks) as the primary price feed.
  // If the server also emits futures:price sub-second ticks, attach a second listener.
  socket.on("futures:update", futuresUpdateHandler);

  // futures:price is optional — attach if the server sends it for smoother chart updates
  socket.on("futures:price", (data) => {
    try {
      if (!data) return;
      const price = parseFloat(data.mark_price ?? data.last_price);
      if (!price || !Number.isFinite(price)) return;
      handlePriceTick(price, data.timestamp || Date.now(), data.symbol || "");
    } catch (_) {}
  });

  listenerAttached = true;
}

// ─── Stream setup ─────────────────────────────────────────────────────────────

function _setupStream({ symbolInfo, resolution, onRealtimeCallback, subscriberUID, lastDailyBar }) {
  const channelString = symbolInfo.name; // e.g. "BTCUSDT_PERP"
  const handler = { id: subscriberUID, callback: onRealtimeCallback };

  let subscriptionItem = channelToSubscription.get(channelString);
  if (subscriptionItem) {
    // Already subscribed — just add this handler
    subscriptionItem.handlers.push(handler);
    ensureFuturesListener();
    return;
  }

  subscriptionItem = {
    subscriberUID,
    resolution,
    lastDailyBar: lastDailyBar ?? null,
    handlers: [handler],
  };
  channelToSubscription.set(channelString, subscriptionItem);
  ensureFuturesListener();
}

// ─── Public API (called by datafeedFutures.js) ────────────────────────────────

export async function subscribeFuturesOnStream(
  symbolInfo,
  resolution,
  onRealtimeCallback,
  subscriberUID,
  onResetCacheNeededCallback,
  lastDailyBar
) {
  const params = { symbolInfo, resolution, onRealtimeCallback, subscriberUID, lastDailyBar };

  if (socket) {
    _setupStream(params);
  } else {
    // Socket not ready yet — store and apply when setSharedSocket() is called
    pendingStreamParams = params;
  }
}

export function unsubscribeFuturesFromStream(subscriberUID) {
  for (const [channelString, subscriptionItem] of channelToSubscription) {
    const idx = subscriptionItem.handlers.findIndex((h) => h.id === subscriberUID);
    if (idx !== -1) {
      subscriptionItem.handlers.splice(idx, 1);
      if (subscriptionItem.handlers.length === 0) {
        channelToSubscription.delete(channelString);
      }
      break;
    }
  }
}

/**
 * Call when leaving the futures trade page.
 * Removes the listeners but does NOT disconnect the socket (SocketContext owns it).
 */
export function disconnectFuturesChartSocket() {
  if (socket && futuresUpdateHandler) {
    socket.off("futures:update", futuresUpdateHandler);
    socket.off("futures:price",  futuresUpdateHandler);
    futuresUpdateHandler = null;
    listenerAttached = false;
  }
  channelToSubscription.clear();
  pendingStreamParams = null;
}

export function isSocketReady() {
  return socket !== null && socket.connected;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getStartOfMinute(timestamp) {
  const d = new Date(timestamp);
  d.setSeconds(0, 0);
  return d.getTime();
}
